import { Box, Stack, Typography, useTheme, Link } from '@mui/material';
import dayjs from 'dayjs';

import { CustomAvatar } from '@app/components/custom-avatar';
import { HighlightMentions } from '@app/modules/task-board';
import { Reactions } from '@app/components/reaction';

const ChatMessage = ({ message }: any) => {
  const theme = useTheme();
  const senderName = `${String(message?.firstName ?? '')} ${String(
    message?.lastName ?? ''
  )}`?.trim();

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <CustomAvatar
          name={senderName}
          src={message?.profileImage ?? undefined}
          color={'default'}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            width: '35px',
            height: '35px',
            fontSize: '12px',
          }}
        />
        <Box flex={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight="bold">{senderName}</Typography>
            <Link underline="hover" sx={{ fontSize: '0.85rem', color: theme.palette.grey[700] }}>
              {dayjs(message?.createdAt).format('YYYY/MM/DD HH:mm')}
            </Link>
          </Stack>

          <Box
            sx={{
              backgroundColor: theme.palette.common.white,
              borderRadius: 1,
              p: 1.5,
              border: `0.5px solid ${theme.palette.grey[300]}`,
              maxWidth: '600px',
            }}
          >
            <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
              <HighlightMentions text={message?.name} />
            </Typography>
          </Box>

          {message.reactions?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Reactions reactionData={message.reactions} readOnly={true} />
            </Box>
          )}

          {message.replies?.map((reply: any, replyIdx: number) => {
            const replySender = `${String(reply?.firstName ?? '')} ${String(
              reply?.lastName ?? ''
            )}`?.trim();
            return (
              <Stack key={reply?.id ?? replyIdx} direction="row" spacing={2} sx={{ mt: 1, ml: 3 }}>
                <CustomAvatar
                  name={replySender}
                  src={reply?.profileImage ?? undefined}
                  color={'default'}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    width: '30px',
                    height: '30px',
                    fontSize: '10px',
                  }}
                />
                <Box flex={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                      {replySender || 'Unknown'}
                    </Typography>
                    <Link
                      underline="hover"
                      sx={{ fontSize: '0.75rem', color: theme.palette.grey[700] }}
                    >
                      {dayjs(reply?.createdAt ?? reply?.timestamp).format('YYYY/MM/DD HH:mm')}
                    </Link>
                  </Stack>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.common.white,
                      borderRadius: 1,
                      p: 1.5,
                      border: `0.5px solid ${theme.palette.grey[300]}`,
                      maxWidth: '450px',
                    }}
                  >
                    <Typography sx={{ whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                      {reply?.name ?? reply?.text}
                    </Typography>
                  </Box>
                  {reply.reactions?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Reactions reactionData={reply.reactions} readOnly={true} />
                    </Box>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Box>
      </Stack>
    </Stack>
  );
};

export { ChatMessage };
